// SignUpForm.tsx
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Builder } from '@builder.io/react';
import { Formik, FormikErrors, FormikHelpers, FormikTouched } from 'formik';
import { useHistory } from 'react-router-dom';

import { isUrl, validateEmailInput } from '../../GlobalFunctions';
import { getCountryOfOrigin, myMemoize, validatePassword as vp } from '../../GlobalFunctions';
import { validateLicenseCode as vl, createTeacherAccount } from '../../BackendInterface';
import ConditionalWrapper from '../ConditionalWrapper';

import TextInput from '../TextInput';
import UsernameInput from '../UsernameInput/UsernameInput';
import LicenseCodeInput from '../LicenseCodeInput';
import SignUpLegalese from '../SignUpLegalese/SignUpLegalese';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import GoogleButton from '../ExternalProviderButtons/GoogleSignUpButton';
import CleverButton from '../ExternalProviderButtons/CleverSignUpButton';
import ClassLinkButton from '../ExternalProviderButtons/ClassLinkSignUpButton';
import { useIsFederatedSignInAvailable } from '../../useIsFederatedSignInAvailable';
import SignUpEpilogue from '../SignUpEpilogue';
import StatusUpdate from '../StatusUpdate';
import { SignUpData } from '@peekapak/types/DataProtocolTypes';
import { logger } from '../../../src/error-tracker';

//
// VisitorCollectorCard replacement
//
const CardContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{ borderRadius: 5, boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.5)', overflow: 'hidden', marginBottom: '2rem' }}
  >
    {children}
  </div>
);

const CardTitle: React.FC<{ children: React.ReactNode; titleAdditionalStyle?: React.CSSProperties }> = ({
  children,
  titleAdditionalStyle,
}) => (
  <div
    style={{
      color: 'white',
      width: '100%',
      fontFamily: 'GT-Walsheim-Pro-Regular',
      fontSize: '3rem',
      margin: '0 auto',
      textAlign: 'center',
      borderTopRightRadius: 5,
      borderTopLeftRadius: 5,
      lineHeight: '2.5em',
      backgroundColor: '#007BFF',
      ...titleAdditionalStyle,
    }}
  >
    {children}
  </div>
);

const CardContent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ padding: '2.5em', fontFamily: 'GT-Walsheim-Pro-Regular' }}>{children}</div>
);

//
// ErrorDisplay mimic
//
const ErrorDisplay: React.FC<{ title: string; subtitle?: string; onRetry: () => void }> = ({
  title,
  subtitle,
  onRetry,
}) => (
  <div style={{ textAlign: 'center', padding: '2rem' }}>
    <h2>{title}</h2>
    {subtitle && <p>{subtitle}</p>}
    <PrimaryButton onClick={onRetry}>Start Over</PrimaryButton>
  </div>
);

//
// Props
//
interface Props {
  formDescription?: string;
  trial?: boolean;
  curriculum: string;
  hasFormDescription: boolean;

  // Builder config
  titleColor?: string;
  titleBackgroundColor?: string;
  descriptionColor?: string;
  buttonBackgroundColor?: string;
  buttonTextColor?: string;
  buttonText?: string;
}

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  optIn: boolean;
  touPp: boolean;
  licenseCode: string;
}

const isObject = (obj: unknown) => obj !== null && typeof obj === 'object';
const isFunction = (obj: unknown) => typeof obj === 'function';
const isPromise = (val: unknown) => isObject(val) && isFunction((val as any).then);

//
// Component
//
const SignUpForm: React.FC<Props> = ({
  formDescription,
  trial,
  curriculum,
  hasFormDescription,
  titleColor = 'white',
  titleBackgroundColor = '#007BFF',
  descriptionColor = '#444',
  buttonBackgroundColor = '#007BFF',
  buttonTextColor = '#fff',
  buttonText = 'Register Now',
}) => {
  const history = useHistory();
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const cache = useRef({});
  const [combinedState, setCombinedState] = useState({
    isStillChecking: true,
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      optIn: false,
      touPp: false,
      licenseCode: '',
    },
  });
  const [status, setStatus] = useState<'form' | 'loading' | 'error'>('form');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const isFederatedSignInAvailable = useIsFederatedSignInAvailable();

  useEffect(() => {
    let mounted = true;
    async function getCountry() {
      const country = await getCountryOfOrigin();
      const isUserFromUS = country === 'USA';
      if (mounted) {
        setCombinedState((prev) => ({
          isStillChecking: false,
          initialValues: { ...prev.initialValues, optIn: isUserFromUS },
        }));
      }
    }
    getCountry();
    return () => {
      mounted = false;
    };
  }, []);

  const validateFirstName = (fname: string) => {
    if (!fname) return 'Please provide your first name';
    if (isUrl(fname)) return 'Please provide a valid first name';
    return '';
  };
  const validateLastName = (lname: string) => {
    if (!lname) return 'Please provide your last name';
    if (isUrl(lname)) return 'Please provide a valid last name';
    return '';
  };
  const validateTou = (touPp: boolean) => (touPp ? '' : `Please accept Peekapak’s Terms of Use and Privacy Policy`);
  const validateOptin = (_optIn: boolean) => '';
  const validatePassword = (password: string) => vp(password);

  async function _validateLicenseCode(code: string) {
    if (!code) return '';
    if (code.length < 14 && code.length > 0) return 'Please enter a complete license code';
    const cleanedCode = code.replace(/-/g, '');
    setIsCheckingCode(true);
    const valid = await vl(cleanedCode);
    setIsCheckingCode(false);
    return valid ? '' : 'The license code entered is invalid';
  }
  const validateLicenseCode = myMemoize(cache.current, _validateLicenseCode);

  const validate = async (values: FormValues) => {
    const errors: FormikErrors<FormValues> = {};
    const validators: Record<string, any> = {
      firstName: validateFirstName,
      lastName: validateLastName,
      email: validateEmailInput,
      password: validatePassword,
      touPp: validateTou,
      optIn: validateOptin,
      licenseCode: validateLicenseCode,
    };
    for (const field of Object.keys(combinedState.initialValues)) {
      const data = values[field as keyof FormikErrors<FormValues>];
      const fn = validators[field as keyof FormikErrors<FormValues>];
      if (fn) {
        const maybePromise = fn(data);
        const validationResult = isPromise(maybePromise) ? await maybePromise : maybePromise;
        if (validationResult) errors[field as keyof FormikErrors<FormValues>] = validationResult;
      }
    }
    return errors;
  };

  const onSubmitIntercept = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    console.log('✅ Submit called with values:', values); // Debug log
    if (isCheckingCode) return;
    setStatus('loading');
    const payload: SignUpData = {
      type: 'manual',
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      password: values.password,
      optIn: values.optIn,
      touPp: values.touPp,
      redeemableCode: values.licenseCode.replace(/-/g, ''),
      curriculum,
    };

    try {
      createTeacherAccount(
        payload,
        () => {
          setSubmitting(false);
          history.push('/registrationConfirmation');
        },
        (errorObject) => {
          logger.logException(new Error(errorObject.message || errorObject.error));
          setSubmitting(false);
          setStatus('error');
          setErrorMessage(errorObject.message || 'Sorry, we had a problem creating your account.');
        },
      );
    } catch (err) {
      console.error('❌ Error in onSubmitIntercept:', err);
      setSubmitting(false);
      setStatus('error');
      setErrorMessage('Unexpected error occurred');
    }
  };

  //
  // Render states
  //
  if (status === 'loading') {
    return (
      <StatusUpdate
        pkToEightBackground
        pictureUrl=''
        contentTitle='Creating your account...'
        contentSubtitle='Please give us a moment!'
        isShowSpinner={true}
      />
    );
  }

  if (status === 'error') {
    return (
      <ErrorDisplay
        title='Sorry, we had a problem creating your account:'
        subtitle={errorMessage}
        onRetry={() => setStatus('form')}
      />
    );
  }

  //
  // Main form
  //
  return (
    <ConditionalWrapper
      condition={trial}
      wrapper={(children) => (
        <CardContainer>
          <CardTitle titleAdditionalStyle={{ backgroundColor: titleBackgroundColor, color: titleColor }}>
            Register for a Free Account
          </CardTitle>
          <CardContent>{children}</CardContent>
        </CardContainer>
      )}
    >
      <Formik
        initialValues={combinedState.initialValues}
        validate={validate}
        onSubmit={onSubmitIntercept}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            {hasFormDescription && formDescription && (
              <p style={{ color: descriptionColor, marginBottom: '1.5rem' }}>{formDescription}</p>
            )}

            <TextInput
              label='First Name'
              name='firstName'
              value={values.firstName}
              error={touched.firstName && errors.firstName}
              onChange={handleChange}
            />

            <TextInput
              label='Last Name'
              name='lastName'
              value={values.lastName}
              error={touched.lastName && errors.lastName}
              onChange={handleChange}
            />

            <UsernameInput
              label='Email'
              name='email'
              value={values.email}
              error={touched.email && errors.email}
              onChange={handleChange}
            />

            <TextInput
              label='Password'
              name='password'
              type='password'
              value={values.password}
              error={touched.password && errors.password}
              onChange={handleChange}
            />

            <LicenseCodeInput
              label='License Code'
              name='licenseCode'
              value={values.licenseCode}
              error={touched.licenseCode && errors.licenseCode}
              onChange={handleChange}
            />

            <SignUpLegalese
              name='touPp'
              checked={values.touPp}
              error={touched.touPp && errors.touPp}
              onChange={handleChange}
            />

            {/* ✅ Native button wrapper ensures submit works */}
            <div style={{ marginTop: '2rem' }}>
              <button type='submit' disabled={isSubmitting} style={{ all: 'unset', width: '100%' }}>
                <PrimaryButton
                  style={{
                    backgroundColor: buttonBackgroundColor,
                    color: buttonTextColor,
                    width: '100%',
                  }}
                >
                  {buttonText}
                </PrimaryButton>
              </button>
            </div>

            {/* Wrapper included, so its 2rem top margin goes with the buttons
                rather than leaving dead space (PE-1784). */}
            {isFederatedSignInAvailable && (
              <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                <GoogleButton
                  licenseCode={values.licenseCode}
                  curriculum={curriculum}
                  onClick={() => console.log('Google sign-up clicked')}
                />
                <CleverButton
                  licenseCode={values.licenseCode}
                  curriculum={curriculum}
                  onClick={() => console.log('Clever sign-up clicked')}
                />
                <ClassLinkButton
                  licenseCode={values.licenseCode}
                  curriculum={curriculum}
                  onClick={() => console.log('ClassLink sign-up clicked')}
                />
              </div>
            )}

            <SignUpEpilogue />
          </form>
        )}
      </Formik>
    </ConditionalWrapper>
  );
};

export default SignUpForm;
